import { useEffect, useState } from 'react';
import { Users, Clock } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Real-data social-proof badge (Booking.com / Doctolib pattern).
 *
 * Fetches /api/widget/{widgetId}/social-proof which returns:
 *   - recent_bookings_7d : reservations in the last 7 days
 *   - bookings_30d      : reservations in the last 30 days
 *   - last_booking_at   : ISO timestamp of most recent reservation
 *
 * Rendering rules:
 *   - Hide entirely if there's not enough activity to be meaningful
 *     (≥ 3 bookings in the last 7 days OR a booking in the last 48h)
 *   - Pick the strongest message available (recency > volume)
 */
export default function SocialProofBadge({ widgetId, className = '' }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!widgetId) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/widget/${widgetId}/social-proof`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setStats(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [widgetId]);

  if (!stats) return null;

  const lastAt = stats.last_booking_at ? new Date(stats.last_booking_at) : null;
  const hoursSinceLast = lastAt
    ? Math.max(0, Math.round((Date.now() - lastAt.getTime()) / 3_600_000))
    : null;
  const recent7 = stats.recent_bookings_7d || 0;

  // Decide which message has the most impact
  let message = null;
  let Icon = Users;
  if (hoursSinceLast != null && hoursSinceLast <= 2) {
    Icon = Clock;
    message = hoursSinceLast === 0
      ? 'Dernière réservation il y a quelques minutes'
      : `Dernière réservation il y a ${hoursSinceLast}h`;
  } else if (recent7 >= 3) {
    message = `${recent7} personnes ont réservé cette semaine`;
  } else if (hoursSinceLast != null && hoursSinceLast <= 24) {
    Icon = Clock;
    message = `Dernière réservation il y a ${hoursSinceLast}h`;
  } else {
    return null;
  }

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 ${className}`}
      data-testid="social-proof-badge"
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
      </span>
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="font-medium">{message}</span>
    </div>
  );
}
